import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'lodash';
import { useLocation } from 'react-use';

import { EXPERIMENTS_DEFAULT_PAGE_SIZE } from '../constants';
import { extractExperimentSearchData } from '../utils';
import {
  EXPERIMENTS_PARAM_DEFAULT_OWNER,
  EXPERIMENTS_PARAM_OWNER,
  EXPERIMENTS_PARAM_SEARCH_KEY,
  EXPERIMENTS_TAB_VIEW,
  EXPERIMENTS_VIEW,
  EXPERIMENTS_VIEW_CALENDAR,
} from '@/common/const';
import { useAppNavigate } from '@/utils/navigation';
import { deepClone } from '@/utils/object';
import { extractParamValueByName, getUrlWithParams } from '@/utils/url';
import { getLoggedUserEmail } from '@/utils/user';

export type ExperimentTabView = 'all' | 'my';

export interface ExperimentsUrlState {
  searchVal: string;
  pageNumber: number;
  pageSize: number;
  /** Filters to pass to the <DashboardTable> (includes display-only keys) */
  tableFilters: { [key: string]: any };
  /** Filters to pass to the API query */
  queryFilters: { [key: string]: any };
  tabView: ExperimentTabView;
  showCalendar: boolean;
  updateUrl: (payload: { [key: string]: any }) => void;
  onTabChange: (activeKey: ExperimentTabView) => void;
  onViewToggle: (showCalendar: boolean) => void;
}

export function useExperimentsUrlState(): ExperimentsUrlState {
  const { navigateTo } = useAppNavigate();
  const location = useLocation();

  const [searchVal, setSearchVal] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(EXPERIMENTS_DEFAULT_PAGE_SIZE);
  const [tableFilters, setTableFilters] = useState<{ [key: string]: any }>({});
  const [queryFilters, setQueryFilters] = useState<{ [key: string]: any }>({});
  const [tabView, setTabView] = useState<ExperimentTabView>('all');
  const [showCalendar, setShowCalendar] = useState(false);

  const navigateRef = useRef(navigateTo);
  navigateRef.current = navigateTo;

  const debouncedNavigate = useMemo(
    () => debounce((url: string) => navigateRef.current(url), 500),
    [],
  );

  const updateUrl = useCallback(
    (payload: { [key: string]: any }) => {
      debouncedNavigate(getUrlWithParams('/t/experiments', payload));
    },
    [debouncedNavigate],
  );

  useEffect(() => {
    const searchUrl = location.search;
    const currentView =
      extractParamValueByName(searchUrl, EXPERIMENTS_VIEW) || '';
    const currentTabView = (extractParamValueByName(
      searchUrl,
      EXPERIMENTS_TAB_VIEW,
    ) || 'all') as ExperimentTabView;
    const isCalendarView = currentView === EXPERIMENTS_VIEW_CALENDAR;

    setShowCalendar(isCalendarView);
    setTabView(currentTabView);

    const {
      searchVal: sv,
      pageNumber: pn,
      pageSize: ps,
      filters,
    } = extractExperimentSearchData(searchUrl);

    const tableFiltersClone = deepClone(filters);
    if (currentTabView === 'my') {
      tableFiltersClone[EXPERIMENTS_PARAM_DEFAULT_OWNER] = getLoggedUserEmail();
    }

    const queryFiltersClone = deepClone(filters);
    if (currentTabView === 'my') {
      queryFiltersClone[EXPERIMENTS_PARAM_OWNER] = getLoggedUserEmail();
    }

    setSearchVal(sv);
    setPageNumber(pn);
    setPageSize(ps);
    setTableFilters({
      ...tableFiltersClone,
      [EXPERIMENTS_PARAM_SEARCH_KEY]: sv,
    });
    setQueryFilters({
      ...queryFiltersClone,
      [EXPERIMENTS_PARAM_SEARCH_KEY]: sv,
    });
  }, [location.href]);

  const onTabChange = useCallback(
    (activeKey: ExperimentTabView) => {
      const payload: { [key: string]: any } = {
        ...tableFilters,
        [EXPERIMENTS_TAB_VIEW]: activeKey,
      };
      if (activeKey === 'my') {
        payload[EXPERIMENTS_PARAM_DEFAULT_OWNER] = getLoggedUserEmail();
      } else {
        delete payload[EXPERIMENTS_PARAM_DEFAULT_OWNER];
      }
      updateUrl(payload);
    },
    [tableFilters, updateUrl],
  );

  const onViewToggle = useCallback(
    (toCalendar: boolean) => {
      const payload = { ...tableFilters };
      if (toCalendar) {
        payload[EXPERIMENTS_VIEW] = EXPERIMENTS_VIEW_CALENDAR;
      } else {
        delete payload[EXPERIMENTS_VIEW];
      }
      updateUrl(payload);
    },
    [tableFilters, updateUrl],
  );

  return {
    searchVal,
    pageNumber,
    pageSize,
    tableFilters,
    queryFilters,
    tabView,
    showCalendar,
    updateUrl,
    onTabChange,
    onViewToggle,
  };
}
